/**
 * Deepinfra Data Source - v13 Section 6.10
 *
 * Direct fetcher for Deepinfra's model list.
 * Deepinfra provides zero-data-retention inference for open models.
 *
 * ╔══════════════════════════════════════════════════════════════════════════════╗
 * ║  NO HARDCODED MODEL DATA - All model info comes from DeepInfra API           ║
 * ║                                                                              ║
 * ║  DeepInfra API provides: model ID, owned_by                                  ║
 * ║  DeepInfra API does NOT provide: pricing, context window, max completion     ║
 * ║  These should be fetched from llm-prices.com or user-configured              ║
 * ╚══════════════════════════════════════════════════════════════════════════════╝
 *
 * @see https://deepinfra.com/models
 */
/**
 * Create display name from model ID using generic cleanup
 * No hardcoded mapping - just transform the ID into readable format
 */
const createDisplayName = (modelId) => {
    // Extract model name from "org/model" format
    const parts = modelId.split('/');
    const modelName = parts[parts.length - 1];
    return modelName
        .replace(/-/g, ' ')
        .replace(/(\d+)b/gi, '$1B')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
        + ' (DeepInfra)';
};
/**
 * Filter for text/chat models (exclude embeddings, audio, etc.)
 * This is TYPE-based filtering, not model-name filtering
 */
const isChatModel = (modelId) => {
    const id = modelId.toLowerCase();
    // Exclude non-chat model TYPES (embeddings, audio, etc.)
    if (id.includes('embed'))
        return false;
    if (id.includes('whisper'))
        return false;
    if (id.includes('tts'))
        return false;
    if (id.includes('reranker'))
        return false;
    if (id.includes('clip'))
        return false;
    // Only include chat/instruct models for text generation
    // This checks for model TYPE indicators, not specific model names
    if (!id.includes('instruct') && !id.includes('chat') && !id.includes('deepseek'))
        return false;
    return true;
};
/**
 * Fetch models directly from Deepinfra API
 * Returns only API-provided data - no hardcoded pricing or limits
 *
 * @param apiKey - Deepinfra API key
 * @returns Array of ModelMetadata for Deepinfra models (with null for missing fields)
 */
export const fetchFromDeepinfra = async (apiKey) => {
    const response = await fetch('https://api.deepinfra.com/v1/openai/models', {
        headers: {
            Authorization: `Bearer ${apiKey}`,
            'Content-Type': 'application/json',
        },
    });
    if (!response.ok) {
        throw new Error(`Deepinfra API returned ${response.status}: ${response.statusText}`);
    }
    const data = await response.json();
    if (!data.data || !Array.isArray(data.data)) {
        throw new Error('Invalid response format from Deepinfra API');
    }
    const models = [];
    for (const model of data.data) {
        // Skip non-chat models (type-based filtering)
        if (!isChatModel(model.id))
            continue;
        // Use only API-provided data - null for missing fields
        models.push({
            id: model.id,
            provider: 'deepinfra',
            displayName: createDisplayName(model.id),
            contextWindow: null, // API doesn't provide this
            maxCompletionTokens: null, // API doesn't provide this
            pricing: null, // API doesn't provide pricing - get from llm-prices.com
            capabilities: [], // API doesn't provide this
            zeroDataRetention: true, // DeepInfra is known to be ZDR - structural knowledge
            isReasoningModel: null, // API doesn't provide this
        });
    }
    return models;
};
